'use client';

import { getSupabaseConfig, refreshSession, setStoredSession, type AuthSession } from './client';

const GUEST_KEY = 'picel_guest_wishlist_v1';
const PROFILE_KEY = 'picel_wishlist_v1';
const MAX_ITEMS = 200;

export function normalizeWishlistIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const result: string[] = [];
  const seen = new Set<string>();
  for (const entry of value) {
    const id = String(entry || '').trim();
    if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,179}$/.test(id) || seen.has(id)) continue;
    seen.add(id);
    result.push(id);
    if (result.length >= MAX_ITEMS) break;
  }
  return result;
}

export function loadGuestWishlist(): string[] {
  if (typeof window === 'undefined') return [];
  try { return normalizeWishlistIds(JSON.parse(localStorage.getItem(GUEST_KEY) || '[]')); }
  catch { return []; }
}

export function saveGuestWishlist(ids: readonly string[]) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(GUEST_KEY, JSON.stringify(normalizeWishlistIds(ids)));
}

export function clearGuestWishlist() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(GUEST_KEY);
}

async function accountUser(session: AuthSession) {
  const { url, anonKey } = getSupabaseConfig();
  if (!anonKey) throw new Error('supabase_public_key_not_configured');
  let currentToken = session.access_token;
  let response = await fetch(`${url}/auth/v1/user`, {
    headers: { apikey: anonKey, Authorization: `Bearer ${currentToken}` },
  });
  if (response.status === 401 || response.status === 403) {
    const refreshed = await refreshSession();
    if (refreshed?.access_token) {
      currentToken = refreshed.access_token;
      response = await fetch(`${url}/auth/v1/user`, {
        headers: { apikey: anonKey, Authorization: `Bearer ${currentToken}` },
      });
    } else {
      setStoredSession(null);
      throw new Error('session_expired');
    }
  }
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.message || 'account_profile_unavailable');
  if (!payload?.id || payload.id !== session.user.id) throw new Error('account_profile_identity_mismatch');
  return payload;
}

export async function loadAccountWishlist(session: AuthSession): Promise<string[]> {
  const user = await accountUser(session);
  return normalizeWishlistIds(user?.user_metadata?.[PROFILE_KEY]);
}

export async function saveAccountWishlist(session: AuthSession, ids: readonly string[]): Promise<string[]> {
  const normalized = normalizeWishlistIds(ids);
  const user = await accountUser(session);
  const { url, anonKey } = getSupabaseConfig();
  const data = { ...(user.user_metadata || {}), [PROFILE_KEY]: normalized };
  const response = await fetch(`${url}/auth/v1/user`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      apikey: anonKey,
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ data }),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.message || 'wishlist_save_failed');
  return normalizeWishlistIds(payload?.user_metadata?.[PROFILE_KEY] ?? normalized);
}

export async function mergeGuestWishlistIntoAccount(session: AuthSession): Promise<string[]> {
  const [accountIds, guestIds] = await Promise.all([
    loadAccountWishlist(session),
    Promise.resolve(loadGuestWishlist()),
  ]);
  const merged = normalizeWishlistIds([...accountIds, ...guestIds]);
  if (guestIds.length) {
    await saveAccountWishlist(session, merged);
    clearGuestWishlist();
  }
  return merged;
}
